"use client"

import { IconTrash } from "@tabler/icons-react"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import { useSavedViews, type SavedView } from "@/contexts/SavedViewsContext"
import { ListTooltip } from "@/components/ListTooltip"
import { cn } from "@/lib/utils"

function truncateText(text: string, maxLength: number): { truncated: string; isTruncated: boolean } {
  if (text.length <= maxLength) {
    return { truncated: text, isTruncated: false }
  }
  return { truncated: text.substring(0, maxLength) + "...", isTruncated: true }
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  })
}

function SavedViewItem({ view, onApply, onDelete }: {
  view: SavedView
  onApply: (view: SavedView) => void
  onDelete: (id: string) => void
}) {
  const titleResult = truncateText(view.title, 28)
  const descriptionResult = truncateText(view.description, 50)

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    onDelete(view.id)
  }

  // Creator name from the createdBy object
  const creatorName = view.createdBy
    ? `${view.createdBy.firstName} ${view.createdBy.lastName}`
    : "Unknown"

  const lastEditedDate = view.lastEditedAt
    ? formatDate(view.lastEditedAt)
    : formatDate(view.createdAt)

  const content = (
    <div
      onClick={() => onApply(view)}
      className={cn(
        "group relative px-[12px] py-[10px] rounded-[6px] cursor-pointer",
        "bg-white border border-[#EDEEF3]",
        "shadow-[0_1px_2px_rgba(0,0,0,0.04)]",
        "hover:border-[#D1D5DB] hover:shadow-[0_2px_4px_rgba(0,0,0,0.06)]",
        "transition-all duration-150"
      )}
    >
      <div className="flex items-start justify-between gap-[8px]">
        <div className="flex-1 min-w-0">
          <h4 className="text-[13px] font-medium text-[#08090A] leading-[18px]">
            {titleResult.truncated}
          </h4>
          {view.description && (
            <p className="text-[11px] text-[#8A8F98] leading-[16px] mt-[2px]">
              {descriptionResult.truncated}
            </p>
          )}
          <div className="flex items-center mt-[6px]">
            <span className="text-[11px] text-[#8A8F98]">
              Created by: {creatorName}
            </span>
            <div className="h-[10px] w-px bg-[#EDEEF3] mx-[8px]" />
            <span className="text-[11px] text-[#8A8F98]">
              Last edited: {lastEditedDate}
            </span>
          </div>
        </div>
        <button
          onClick={handleDelete}
          className={cn(
            "flex items-center justify-center size-[24px] rounded-[4px]",
            "text-[#8A8F98] opacity-0 group-hover:opacity-100",
            "hover:text-[#E74C3C] hover:bg-[#E74C3C]/10",
            "transition-all duration-150"
          )}
        >
          <IconTrash className="size-3.5" />
        </button>
      </div>
    </div>
  )

  // Only wrap with tooltip if title or description is truncated
  if (titleResult.isTruncated || descriptionResult.isTruncated) {
    return (
      <ListTooltip
        side="left"
        items={[
          {
            id: view.id,
            label: view.title,
            sublabel: view.description || undefined,
          },
        ]}
      >
        {content}
      </ListTooltip>
    )
  }

  return content
}

export function SavedViewsSidebar() {
  const { savedViews, isLoading, isSidebarOpen, setIsSidebarOpen, applyView, deleteSavedView } = useSavedViews()

  return (
    <SheetPrimitive.Root open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
      <SheetPrimitive.Portal>
        <SheetPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-black/40",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          )}
        />
        <SheetPrimitive.Content
          className={cn(
            "fixed z-50 flex flex-col bg-white shadow-lg",
            "inset-y-0 right-0 h-full w-[340px] border-l border-[#EDEEF3]",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
            "data-[state=closed]:duration-200 data-[state=open]:duration-300"
          )}
        >
          {/* Header - Clean, no icon */}
          <div className="px-[16px] py-[14px] border-b border-[#EDEEF3]">
            <SheetPrimitive.Title className="text-[14px] font-medium text-[#08090A]">
              Saved Views
            </SheetPrimitive.Title>
            <SheetPrimitive.Description className="text-[12px] text-[#8A8F98] mt-[2px]">
              Click on a view to apply its filters
            </SheetPrimitive.Description>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-[12px]">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-[40px] text-center">
                <div className="size-6 border-2 border-[#654EDA] border-t-transparent rounded-full animate-spin mb-[12px]" />
                <p className="text-[12px] text-[#8A8F98]">Loading saved views...</p>
              </div>
            ) : savedViews.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-[40px] text-center">
                <h4 className="text-[13px] font-medium text-[#08090A] mb-[4px]">
                  No saved views yet
                </h4>
                <p className="text-[12px] text-[#8A8F98] max-w-[180px]">
                  Apply filters and click "Save View" to create your first saved view
                </p>
              </div>
            ) : (
              <div className="space-y-[8px]">
                {savedViews.map((view) => (
                  <SavedViewItem
                    key={view.id}
                    view={view}
                    onApply={applyView}
                    onDelete={deleteSavedView}
                  />
                ))}
              </div>
            )}
          </div>
        </SheetPrimitive.Content>
      </SheetPrimitive.Portal>
    </SheetPrimitive.Root>
  )
}
